/**
 * Feature Flags Configuration
 *
 * Controls feature visibility across different environments.
 * Flags are set at build-time via environment variables.
 *
 * @example
 * // In .env.development
 * VITE_FEATURE_EMAIL_OPEN_BUTTON=true
 * VITE_FEATURE_EMAIL_FORM_TAB=true
 *
 * // In .env.production
 * VITE_FEATURE_EMAIL_OPEN_BUTTON=false
 * VITE_FEATURE_EMAIL_FORM_TAB=false
 */

/**
 * Email-related feature flags
 */

/**
 * Controls visibility of the "Open Email Tab" button in ServiceNowButtons component.
 * When false, the button will not be displayed regardless of contact type.
 */
export const FEATURE_EMAIL_OPEN_BUTTON = import.meta.env.VITE_FEATURE_EMAIL_OPEN_BUTTON === 'true';

/**
 * Controls visibility of the "Email Form" tab in CallManagement component.
 * When false, the tab will not be displayed regardless of contact type.
 */
export const FEATURE_EMAIL_FORM_TAB = import.meta.env.VITE_FEATURE_EMAIL_FORM_TAB === 'true';

/**
 * Controls visibility of the "Procedura Guidata" (step-by-step guide) tab
 * in CallManagement component.
 * When false, the tab will not be displayed.
 */
export const FEATURE_STEP_BY_STEP_GUIDE_TAB = import.meta.env.VITE_FEATURE_STEP_BY_STEP_GUIDE_TAB === 'true';

/**
 * Controls visibility of the "Contact History" tab in CallManagement component.
 * When false, the tab will not be displayed.
 */
export const FEATURE_CONTACT_HISTORY = import.meta.env.VITE_FEATURE_CONTACT_HISTORY === 'true';

/**
 * Controls where the Contact History tab is displayed.
 * When true it is shown in CallManagement; when false in TabsManagement.
 */
export const FEATURE_CONTACT_HISTORY_UP = import.meta.env.VITE_FEATURE_CONTACTHISTORY_UP === 'true';

/**
 * Task-related feature flags
 */

/**
 * Controls visibility of the Service Area KPI counters (SE, AR, CL) in TopBar component.
 * When false, the 3 counters (Service, Area, Cluster) will not be displayed for TASK contacts.
 */
export const FEATURE_TASK_ALERT_COUNTERS = import.meta.env.VITE_FEATURE_TASK_ALERT_COUNTERS === 'true';

/**
 * Chat-related feature flags
 */

/**
 * Controls visibility of the task counter alert in CallInformations component.
 * When false, the "ticket aperti in giornata" alert will not be displayed in the caller section.
 */
export const FEATURE_CHAT_ALERT = import.meta.env.VITE_FEATURE_CHAT_ALERT === 'true';

/**
 * Controls whether the "Caduta Linea" button is enabled for CHAT contacts.
 * When false, the button will be disabled for CHAT contact types.
 * When true, the button will be enabled for all contact types (default behavior).
 */
export const FEATURE_CHAT_LINE_DROP = import.meta.env.VITE_FEATURE_CHAT_LINE_DROP === 'true';

/**
 * Voice-related feature flags
 */

/**
 * Controls whether outbound call worknotes are automatically added to open TASK/CHAT/EMAIL tickets.
 * When false, worknotes for outbound calls will NOT be added to active tickets or voice ticket updates.
 * When true (default behavior), worknotes are added to active tickets immediately or to voice tickets via destroy.
 */
export const FEATURE_WORKNOTE_OUTBOUND_CALL = import.meta.env.VITE_FEATURE_WORKNOTE_OUTBOUND_CALL === 'true';

/**
 * Controls visibility and payload fields for the blocked support-ticket option.
 */
export const FEATURE_BLOCKED = import.meta.env.VITE_FEATURE_BLOCKED === 'true';

/**
 * Controls visibility of the Console Comandi tab.
 * When true, the tab is only shown to users whose username contains RPY3PPS or SCIMEALB.
 * When false, the tab is shown to everyone.
 */
//visibile per cert e non prod
export const FEATURE_SHOW_CONSOLE_COMANDI = import.meta.env.VITE_FEATURE_SHOW_CONSOLE_COMANDI === 'true';

/**
 * Caller section (consulente mobile) feature flags
 */

/**
 * Controls whether the caller section uses the new consulente mobile logic
 * (Frazionario mismatch detection, hybrid caller/office data, automatic
 * Frazionario/PDL search without a username) for TASK/EMAIL and CHAT contacts.
 * When false, the legacy behavior is used: the username lookup always
 * overwrites the caller section unconditionally, with no Frazionario
 * comparison and no automatic search when a username is absent.
 */
export const FEATURE_CONSULENTE_MOBILE_LOGIC = import.meta.env.VITE_FEATURE_CONSULENTE_MOBILE_LOGIC === 'true';

/**
 * Controls visibility of the username search in the CallInformations PDL search area.
 * When false, only Frazionario, PDL and search are displayed, without the clear button.
 */
export const FEATURE_SHOW_RICERCA_USERNAME = import.meta.env.VITE_FEATURE_SHOW_RICERCA_USERNAME === 'true';

/**
 * All feature flags object (useful for debugging/logging)
 */
export const ALL_FEATURE_FLAGS = {
  FEATURE_EMAIL_OPEN_BUTTON,
  FEATURE_EMAIL_FORM_TAB,
  FEATURE_STEP_BY_STEP_GUIDE_TAB,
  FEATURE_CONTACT_HISTORY,
  FEATURE_CONTACT_HISTORY_UP,
  FEATURE_TASK_ALERT_COUNTERS,
  FEATURE_CHAT_ALERT,
  FEATURE_CHAT_LINE_DROP,
  FEATURE_WORKNOTE_OUTBOUND_CALL,
  FEATURE_SHOW_CONSOLE_COMANDI,
  FEATURE_CONSULENTE_MOBILE_LOGIC,
  FEATURE_SHOW_RICERCA_USERNAME,
  FEATURE_BLOCKED
} as const;

/**
 * Type-safe feature flag names
 */
export type FeatureFlagName = keyof typeof ALL_FEATURE_FLAGS;
